import { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '@/services/api';
import { useChain, useStore } from '@/stores';
import { setAuthToken } from '@/services/api/client';
import { getWalletForChain, onboardingRouteForChain } from '@/utils/wallets';
import { detectActiveChain } from '@/utils/chainDetection';
import { ROUTES } from '@/config/routes';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type AuthMode = 'signin' | 'signup';

export function LoginModal({ isOpen, onClose }: LoginModalProps) {
  const navigate = useNavigate();
  const { selectedChain, setSelectedChain } = useChain();
  const [mode, setMode] = useState<AuthMode>('signin');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const isSignup = mode === 'signup';

  const resetForm = () => {
    setUsername('');
    setEmail('');
    setPassword('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const response = isSignup
        ? await authAPI.signup({ username, email, password })
        : await authAPI.login({ username, password });

      // Store token in localStorage and API client
      setAuthToken(response.token);

      // Hydrate the full user shape immediately so wallet-gated routes do not
      // render against the minimal login payload before the next app reload.
      const meResponse = await authAPI.me();

      // Update Zustand store
      useStore.setState((state) => ({
        auth: {
          ...state.auth,
          user: meResponse.user,
          token: response.token,
          isAuthenticated: true,
          isLoading: false,
          // Disclaimer gate state is sourced from /api/me (the login/signup
          // response carries it too, but me() is the single source of truth).
          betaDisclaimer: meResponse.betaDisclaimer ?? null
        }
      }));

      // Bring the user to the chain they're actually on (active strategy), rather
      // than the persisted/default chain, so a Sol trader doesn't land on BSC.
      const targetChain = (await detectActiveChain(meResponse.user)) ?? selectedChain;
      if (targetChain !== selectedChain) setSelectedChain(targetChain);

      // Close modal + reset form
      onClose();
      resetForm();

      // Route on the resolved chain: onboarding if no wallet there, else the
      // stored redirect, else straight to the trading dashboard.
      const redirectPath = sessionStorage.getItem('redirectAfterLogin');
      if (!getWalletForChain(meResponse.user, targetChain)?.address) {
        navigate(onboardingRouteForChain(targetChain));
      } else if (redirectPath) {
        sessionStorage.removeItem('redirectAfterLogin');
        navigate(redirectPath);
      } else {
        navigate(ROUTES.TRADING.ROOT);
      }
    } catch (err: any) {
      console.error(isSignup ? 'Signup error:' : 'Login error:', err);
      setError(
        err.message || (isSignup ? 'Could not create your account' : 'Invalid username or password')
      );
    } finally {
      setIsLoading(false);
    }
  };

  const toggleMode = () => {
    setError(null);
    setMode((m) => (m === 'signin' ? 'signup' : 'signin'));
  };

  const handleClose = () => {
    if (!isLoading) {
      setError(null);
      resetForm();
      onClose();
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="relative w-full max-w-md transform overflow-hidden rounded-2xl border bg-white p-6 shadow-xl transition-all dark:border-white/[0.08] dark:bg-abyss-100/95 dark:shadow-glow-violet dark:backdrop-blur-xl">
                {/* top accent */}
                <div className="pointer-events-none absolute inset-x-0 top-0 hidden h-px bg-gradient-to-r from-transparent via-violet-400/70 to-transparent dark:block" />
                <div className="mb-1 flex items-center justify-between">
                  <Dialog.Title className="font-display text-xl font-semibold text-gray-900 dark:text-white">
                    {isSignup ? 'Create your account' : 'Welcome back'}
                  </Dialog.Title>
                  <button
                    onClick={handleClose}
                    disabled={isLoading}
                    className="rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-800 hover:text-gray-300 dark:hover:bg-white/[0.06]"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
                <p className="mb-4 text-sm text-gray-500">
                  {isSignup ? 'Start your strategy in a couple of minutes.' : 'Sign in to get back to the loop.'}
                </p>

                <form onSubmit={handleSubmit} className="space-y-3">
                  {error && (
                    <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-3">
                      <p className="text-sm text-red-400">{error}</p>
                    </div>
                  )}

                  <div>
                    <label
                      htmlFor="username"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      Username
                    </label>
                    <input
                      id="username"
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      disabled={isLoading}
                      required
                      className="mt-1 block w-full rounded-xl border bg-white px-3.5 py-2.5 text-sm text-gray-900 placeholder-gray-500 transition-all focus:outline-none disabled:opacity-50 dark:border-white/10 dark:bg-white/[0.04] dark:text-white dark:placeholder-gray-600 dark:focus:border-indigo-400/60 dark:focus:ring-2 dark:focus:ring-indigo-500/30 dark:disabled:bg-gray-900"
                      placeholder="Enter your username"
                    />
                  </div>

                  {isSignup && (
                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                      >
                        Email
                      </label>
                      <input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled={isLoading}
                        required
                        autoComplete="email"
                        className="mt-1 block w-full rounded-xl border bg-white px-3.5 py-2.5 text-sm text-gray-900 placeholder-gray-500 transition-all focus:outline-none disabled:opacity-50 dark:border-white/10 dark:bg-white/[0.04] dark:text-white dark:placeholder-gray-600 dark:focus:border-indigo-400/60 dark:focus:ring-2 dark:focus:ring-indigo-500/30 dark:disabled:bg-gray-900"
                        placeholder="you@email.com"
                      />
                      <p className="mt-1 text-xs text-gray-500">Used for account recovery.</p>
                    </div>
                  )}

                  <div>
                    <label
                      htmlFor="password"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      Password
                    </label>
                    <input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={isLoading}
                      required
                      minLength={isSignup ? 8 : undefined}
                      autoComplete={isSignup ? 'new-password' : 'current-password'}
                      className="mt-1 block w-full rounded-xl border bg-white px-3.5 py-2.5 text-sm text-gray-900 placeholder-gray-500 transition-all focus:outline-none disabled:opacity-50 dark:border-white/10 dark:bg-white/[0.04] dark:text-white dark:placeholder-gray-600 dark:focus:border-indigo-400/60 dark:focus:ring-2 dark:focus:ring-indigo-500/30 dark:disabled:bg-gray-900"
                      placeholder={isSignup ? 'At least 8 characters' : 'Enter your password'}
                    />
                  </div>

                  <button type="submit" disabled={isLoading} className="btn-aurora w-full !py-3">
                    {isLoading ? (
                      <span className="flex items-center justify-center gap-1.5">
                        <div className="spinner h-4 w-4" />
                        {isSignup ? 'Creating account...' : 'Signing in...'}
                      </span>
                    ) : isSignup ? (
                      'Create account'
                    ) : (
                      'Sign In'
                    )}
                  </button>

                  <p className="pt-1 text-center text-xs text-gray-500">
                    {isSignup ? 'Already have an account?' : 'New here?'}{' '}
                    <button
                      type="button"
                      onClick={toggleMode}
                      disabled={isLoading}
                      className="font-medium text-violet-400 transition-colors hover:text-violet-300 disabled:opacity-50"
                    >
                      {isSignup ? 'Sign in' : 'Create one'}
                    </button>
                  </p>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
